
// Rate limiting utilities to prevent spam and abuse

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
}

class RateLimiter {
  private attempts: Map<string, number[]> = new Map();

  isAllowed(key: string, config: RateLimitConfig): boolean {
    const now = Date.now();
    const windowStart = now - config.windowMs;
    
    // Get existing attempts for this key
    const keyAttempts = this.attempts.get(key) || [];
    
    // Filter out attempts outside the current window
    const recentAttempts = keyAttempts.filter(time => time > windowStart);
    
    // Check if we're over the limit
    if (recentAttempts.length >= config.maxAttempts) {
      return false;
    }
    
    // Add current attempt and update the map
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    
    return true;
  }

  getRemainingTime(key: string, config: RateLimitConfig): number {
    const attempts = this.attempts.get(key) || [];
    if (attempts.length === 0) return 0;
    
    const oldestAttempt = Math.min(...attempts);
    const windowEnd = oldestAttempt + config.windowMs;
    const remaining = Math.max(0, windowEnd - Date.now());
    
    return remaining;
  }

  reset(key: string): void {
    this.attempts.delete(key);
  }
}

// Global rate limiter instance
export const rateLimiter = new RateLimiter();

// Rate limiting configurations
export const RATE_LIMITS = {
  LIKE_SEND: { maxAttempts: 20, windowMs: 60 * 1000 }, // 20 likes per minute
  MESSAGE_SEND: { maxAttempts: 30, windowMs: 60 * 1000 }, // 30 messages per minute
  PROFILE_VIEW: { maxAttempts: 100, windowMs: 60 * 1000 }, // 100 profile views per minute
  AUTH_ATTEMPT: { maxAttempts: 5, windowMs: 15 * 60 * 1000 }, // 5 auth attempts per 15 minutes
} as const;

// Helper function to create rate limit keys
export const createRateLimitKey = (userId: string, action: string): string => {
  return `${userId}:${action}`;
};
